import React from 'react'
import {observer} from 'mobx-react'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {Modal} from '../modal'
import {Box, Button, Flex, Stack, Text} from '../../vanilla'
import useNavigation from '../../hooks/use-navigation'
import {useRapidDeliveryStore} from '../../store/hooks/useStore'
import {rapidBasketCapacityModalContent} from './styles.css'
import { isMobile } from '../../utils/utils'

export const RapidBasketCapacityModal = observer(() => {
  const navigate = useNavigation()
  const {
    rapidBasketCapacityModalOpen,
    rapidBasketCapacityState,
    setRapidBasketCapacityModalOpen,
  } = useRapidDeliveryStore()

  const isNearCapacity = rapidBasketCapacityState === 'nearLimit'
  const isOverLimitAttempt = rapidBasketCapacityState === 'overLimitAttempt'
  const capacityIllustrationSrc = isNearCapacity || isOverLimitAttempt
    ? `${getAssetUrl('static/img/rapid-basket-nearly-full.svg')}`
    : `${getAssetUrl('static/img/rapid-basket-full.svg')}`

  const title = isOverLimitAttempt
    ? 'Your Basket’s Almost Full'
    : isNearCapacity
    ? 'Ready To Checkout?'
    : 'Your Rapid Delivery Basket is Full'
  const description = isOverLimitAttempt
    ? <>This item would take your basket over the limit for Rapid Delivery. Switch to <strong>Same Day</strong> or <strong>FREE Next Day Delivery</strong> for more space.</>
    : isNearCapacity
    ? <>There's not much space left in your basket. Checkout now to secure your <strong>Rapid Delivery</strong>. You can still make changes before paying.</>
    : <>To get more space, switch to a <strong>Same Day</strong> or <strong>FREE Next Day Delivery</strong>. Alternatively, checkout now or remove a few items from your basket.</>

  const closeModal = () => {
    setRapidBasketCapacityModalOpen(false)
  }

  const handleSwitchDeliveryType = () => {
    closeModal()
    if (rapidBasketCapacityState === 'fullLimit' || isOverLimitAttempt) {
      navigate('/select-delivery')
    }
  }

  const handleContinueWithRapid = () => {
    closeModal()
    navigate('/basket')
  }

  return (
    <Modal
      isOpen={rapidBasketCapacityModalOpen && rapidBasketCapacityState !== 'none'}
      onClose={closeModal}
      size={isMobile() ? 'bottom-sm' : 'lg'}
      title={title}
      showTitleBorder={false}
      allowScroll
      dataTestSelector="rapid-basket-capacity-modal"
      dialogContentProps={{
        className: rapidBasketCapacityModalContent
      }}
    >
      <Box paddingX="20px" paddingBottom="20px">
        <Stack spacing="20px">
          <Flex justifyContent="center">
            <Box
              as="img"
              src={capacityIllustrationSrc}
              alt=""
              flexShrink="0"
              style={{width: '114px', height: '116px'}}
              aria-hidden="true"
              data-test-selector="rapid-basket-capacity-animation"
            />
          </Flex>

          <Stack spacing="8px">
            <Text variant="text3" lineHeight="short" data-test-selector="rapid-basket-capacity-description">
              {description}
            </Text>
          </Stack>

          <Flex gap="12px" justifyContent={["center", "flex-end"]} direction={['column-reverse', 'row']}>
            <Button
              variant="outlineDark"
              size="sm"
              width={["full", "auto"]}
              onClick={handleSwitchDeliveryType}
              data-test-selector="rapid-basket-capacity-switch-delivery"
            >
              <Text variant="unstyled" fontSize="inherit" lineHeight="inherit" fontWeight="inherit">
                {isNearCapacity ? 'Continue Shopping' : 'Switch Delivery'}
              </Text>
            </Button>
            <Button
              variant="primary"
              size="sm"
              width={["full", "auto"]}
              onClick={handleContinueWithRapid}
              data-test-selector="rapid-basket-capacity-continue"
            >
              {isNearCapacity ? 'Checkout Now' : 'Continue with Rapid'}
            </Button>
          </Flex>
        </Stack>
      </Box>
    </Modal>
  )
})
